import { SimpleGrid, Table, Group, Stack, Text, Button, Alert, Badge } from '@mantine/core';
import {
  IconAlertTriangle, IconRefresh, IconReportAnalytics, IconTrophy, IconThumbUp,
  IconMinus, IconThumbDown, IconUsers,
} from '@tabler/icons-react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import api from '../../services/api';
import { SectionCard } from '../SectionCard';
import { StatCard } from '../StatCard';
import { EmptyState } from '../EmptyState';
import { TableSkeleton } from '../TableSkeleton';
import { SILENT_ERROR } from '../../lib/queryMeta';
import { fmtAvg, gradeColor, termGradeValue, type GradeReportResponse } from '../../lib/journal';

interface Props {
  classId: string | null;
  subjectId: string | null;
  termId: string | null;
}

/**
 * Baho hisoboti (kontrakt 2.4 — `GET /reports/grades`).
 *
 * DIQQAT: bu endpoint ATAYLAB `reports.ts` dan TASHQARIDA turadi — o'sha router
 * butunlay `FINANCE` bilan qulflangan va o'qituvchi o'z sinfining baho hisobotini
 * ololmasdi. Front shu sabab uni oddiy INTERNAL so'rov sifatida chaqiradi.
 */
export function GradeReport({ classId, subjectId, termId }: Props) {
  const { t } = useTranslation();
  const ready = !!classId && !!subjectId && !!termId;

  const { data, isLoading, isError, isFetching, refetch } = useQuery({
    queryKey: ['grades-report', classId, subjectId, termId],
    queryFn: async () =>
      (await api.get<GradeReportResponse>('/reports/grades', { params: { classId, subjectId, termId } })).data,
    enabled: ready,
    meta: SILENT_ERROR,
  });

  const rows = data?.rows ?? [];
  const s = data?.summary;

  return (
    <Stack gap="md">
      {!!s && (
        <SimpleGrid cols={{ base: 1, sm: 2, lg: 3 }} spacing="md">
          {/* §6.6: `summary.avg` — CHORAK baholarining o'rtachasi (joriy baholarniki emas) */}
          <StatCard icon={IconReportAnalytics} label={t('gradeReport.summaryAvg')} value={fmtAvg(s.avg)} color="brand" hint={t('gradeReport.summaryAvgHint')} />
          <StatCard
            icon={IconUsers}
            label={t('gradeReport.graded')}
            value={`${s.graded ?? 0} / ${s.total ?? 0}`}
            color="blue"
            hint={t('gradeReport.gradedHint')}
          />
          <StatCard icon={IconTrophy} label={t('gradeReport.excellent')} value={s.excellent ?? 0} color="teal" />
          <StatCard icon={IconThumbUp} label={t('gradeReport.good')} value={s.good ?? 0} color="green" />
          <StatCard icon={IconMinus} label={t('gradeReport.satisfactory')} value={s.satisfactory ?? 0} color="yellow" />
          <StatCard icon={IconThumbDown} label={t('gradeReport.poor')} value={s.poor ?? 0} color="red" />
        </SimpleGrid>
      )}

      <SectionCard
        title={t('gradeReport.title')}
        icon={IconReportAnalytics}
        subtitle={data?.term?.name}
        p={0}
      >
        {!ready ? (
          <EmptyState icon={IconReportAnalytics} title={t('journal.selectFilters')} height={180} />
        ) : isLoading ? (
          <TableSkeleton rows={6} cols={4} />
        ) : isError ? (
          <Stack align="center" gap="sm" p="lg">
            <Alert color="red" variant="light" icon={<IconAlertTriangle size={18} />} title={t('common.error')} w="100%">
              {t('gradeReport.loadError')}
            </Alert>
            <Button variant="light" loading={isFetching} leftSection={<IconRefresh size={16} />} onClick={() => void refetch()}>
              {t('terms.retry')}
            </Button>
          </Stack>
        ) : !rows.length ? (
          <EmptyState icon={IconReportAnalytics} title={t('gradeReport.empty')} description={t('gradeReport.emptyDesc')} />
        ) : (
          <Table.ScrollContainer minWidth={760}>
            <Table highlightOnHover verticalSpacing="sm">
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>{t('journal.student')}</Table.Th>
                  <Table.Th w={130} ta="center">{t('gradeReport.gradeCount')}</Table.Th>
                  <Table.Th w={110} ta="center">{t('gradeReport.avg')}</Table.Th>
                  <Table.Th w={110} ta="center">{t('termSummary.suggested')}</Table.Th>
                  <Table.Th w={150} ta="center">{t('gradeReport.termGrade')}</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {rows.map((r) => {
                  const tg = termGradeValue(r.termGrade);
                  return (
                    <Table.Tr key={r.studentId}>
                      <Table.Td fw={500}>{r.fullName}</Table.Td>
                      <Table.Td ta="center">
                        <Badge variant="light" color={r.gradeCount ? 'blue' : 'gray'}>{r.gradeCount ?? 0}</Badge>
                      </Table.Td>
                      <Table.Td ta="center" fw={600}>{fmtAvg(r.avg)}</Table.Td>
                      <Table.Td ta="center">
                        {r.suggested != null
                          ? <Badge variant="light" color={gradeColor(r.suggested)}>{r.suggested}</Badge>
                          : <Text c="dimmed" fz="sm" lh={1.4}>—</Text>}
                      </Table.Td>
                      <Table.Td ta="center">
                        {tg != null ? (
                          <Badge size="lg" color={gradeColor(tg)}>{tg}</Badge>
                        ) : (
                          <Text c="dimmed" fz="xs" lh={1.5}>{t('journal.termNotSet')}</Text>
                        )}
                      </Table.Td>
                    </Table.Tr>
                  );
                })}
              </Table.Tbody>
            </Table>
          </Table.ScrollContainer>
        )}
      </SectionCard>

      {!!rows.length && (
        <Group justify="flex-end">
          <Text c="dimmed" fz="sm" lh={1.5}>{t('common.total')}: {rows.length}</Text>
        </Group>
      )}
    </Stack>
  );
}
